import type { CategorieImmo, Immobilisation } from '@shared/types/Immobilisation';
import type { Ecriture } from '@shared/types/Ecriture';
import { PLAN_COMPTABLE_OHADA } from '@shared/constants/PlanComptable';
import { uuid } from '@shared/utils/uuid';
import { AmortissementsService } from './AmortissementsService';

/** Durées d’usage SYSCOHADA (années) — plus de 5 ans forcés pour tout. */
export const DUREE_AMORTISSEMENT_PAR_CATEGORIE: Record<CategorieImmo, number> = {
  terrain: 0,
  batiment: 20,
  materiel: 10,
  mobilier: 10,
  vehicule: 5,
  informatique: 3,
  incorporel: 5,
};

export function dureeAmortissement(categorie: CategorieImmo): number {
  return DUREE_AMORTISSEMENT_PAR_CATEGORIE[categorie] ?? 5;
}

export function comptesImmobilisation(categorie: CategorieImmo): {
  compte_immo: string;
  compte_amortissement: string;
  compte_dotation: string;
} {
  switch (categorie) {
    case 'vehicule':
      return { compte_immo: '218', compte_amortissement: '281', compte_dotation: '6813' };
    case 'informatique':
      return { compte_immo: '218', compte_amortissement: '281', compte_dotation: '6813' };
    case 'materiel':
      return { compte_immo: '215', compte_amortissement: '281', compte_dotation: '6813' };
    case 'mobilier':
      return { compte_immo: '218', compte_amortissement: '281', compte_dotation: '6813' };
    case 'batiment':
      return { compte_immo: '213', compte_amortissement: '281', compte_dotation: '6813' };
    case 'terrain':
      return { compte_immo: '211', compte_amortissement: '281', compte_dotation: '6813' };
    case 'incorporel':
      return { compte_immo: '202', compte_amortissement: '271', compte_dotation: '6811' };
    default:
      return { compte_immo: '218', compte_amortissement: '281', compte_dotation: '6813' };
  }
}

export function categorieDepuisAvantage(type: string): CategorieImmo {
  if (type === 'vehicule') return 'vehicule';
  if (type === 'logement') return 'batiment';
  return 'materiel';
}

/** Écritures OD : débit 681 / crédit 28 pour la dotation de l’année civile. */
export function genererEcrituresDotation(
  immo: Immobilisation,
  annee: number,
  datePiece = `${annee}-12-31`,
): Ecriture[] {
  if (immo.categorie === 'terrain' || dureeAmortissement(immo.categorie) <= 0) return [];
  const montant = AmortissementsService.dotationPourAnneeCivile(immo, annee);
  if (montant <= 0) return [];
  const periode = datePiece.slice(0, 7);
  const dotation = immo.compte_dotation || '6813';
  const amort = immo.compte_amortissement || '2818';
  const libelle = `Dotation ${annee} — ${immo.libelle}`;
  const ligne = (
    compte: string,
    debit: number,
    credit: number,
  ): Ecriture => ({
    id: uuid('ecr'),
    tenant_id: immo.tenant_id,
    date: datePiece,
    periode,
    journal: 'OD',
    numero_piece: `AMO-${immo.code || immo.id}-${annee}`,
    compte,
    libelle_compte: PLAN_COMPTABLE_OHADA[compte]?.libelle ?? compte,
    libelle,
    debit,
    credit,
    source: 'amortissement',
    groupe_id: immo.id,
    genere_le: new Date().toISOString(),
  });
  return [ligne(dotation, montant, 0), ligne(amort, 0, montant)];
}

function ligneImmo(
  immo: Immobilisation,
  date: string,
  journal: Ecriture['journal'],
  piece: string,
  compte: string,
  debit: number,
  credit: number,
  libelle: string,
  source: Ecriture['source'],
): Ecriture {
  return {
    id: uuid('ecr'),
    tenant_id: immo.tenant_id,
    date,
    periode: date.slice(0, 7),
    journal,
    numero_piece: piece,
    compte,
    libelle_compte: PLAN_COMPTABLE_OHADA[compte]?.libelle ?? compte,
    libelle,
    debit,
    credit,
    source,
    groupe_id: immo.id,
    genere_le: new Date().toISOString(),
  };
}

/** Acquisition : débit 2xx / crédit 401. */
export function genererEcrituresAcquisition(immo: Immobilisation): Ecriture[] {
  const montant = immo.valeur_acquisition;
  if (montant <= 0) return [];
  const date = immo.date_acquisition;
  const piece = `ACQ-${immo.code || immo.id}`;
  const immoC = immo.compte_immo || comptesImmobilisation(immo.categorie).compte_immo;
  const libelle = `Acquisition — ${immo.libelle}`;
  return [
    ligneImmo(immo, date, 'OD', piece, immoC, montant, 0, libelle, 'facture'),
    ligneImmo(immo, date, 'OD', piece, '401', 0, montant, libelle, 'facture'),
  ];
}

/** Cession : sortie 2xx, reprise 28x, plus-value 82 ou moins-value 81. */
export function genererEcrituresCession(immo: Immobilisation): Ecriture[] {
  const date = immo.date_cession;
  if (!date || !immo.cede) return [];
  const debut = Number((immo.date_mise_service || immo.date_acquisition).slice(0, 4));
  const annee = Number(date.slice(0, 4));
  const rang = debut && annee ? annee - debut + 1 : 1;
  const vnc = AmortissementsService.vncFinExercice(immo, Math.max(1, rang));
  const brut = immo.valeur_acquisition;
  const cumul = Math.max(0, brut - vnc);
  const prix = immo.valeur_cession ?? 0;
  const comptes = comptesImmobilisation(immo.categorie);
  const immoC = immo.compte_immo || comptes.compte_immo;
  const amortC = immo.compte_amortissement || comptes.compte_amortissement;
  const piece = `CES-${immo.code || immo.id}`;
  const libelle = `Cession — ${immo.libelle}`;
  const lignes: Ecriture[] = [];
  if (cumul > 0) {
    lignes.push(ligneImmo(immo, date, 'OD', piece, amortC, cumul, 0, libelle, 'cession_immo'));
  }
  lignes.push(ligneImmo(immo, date, 'OD', piece, immoC, 0, brut, libelle, 'cession_immo'));
  if (prix > 0) {
    lignes.push(ligneImmo(immo, date, 'OD', piece, '462', prix, 0, libelle, 'cession_immo'));
  }
  const ecart = prix - vnc;
  if (ecart > 0) {
    lignes.push(ligneImmo(immo, date, 'OD', piece, '82', 0, ecart, libelle, 'cession_immo'));
  } else if (ecart < 0) {
    lignes.push(ligneImmo(immo, date, 'OD', piece, '81', -ecart, 0, libelle, 'cession_immo'));
  }
  return lignes;
}
